import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Plus, Trash2 } from 'lucide-react'
import { api } from '../../config/api'
import AiHsCalculatorChat from './AiHsCalculatorChat'
import ResultCard from './ResultCard'

const normalizeChat = (chat = {}) => ({
    _id: chat._id,
    title: chat.title || 'New chat',
    itemName: chat.itemName || '',
    targetCountry: chat.targetCountry || '',
    sessionId: chat.sessionId || null,
    messages: Array.isArray(chat.messages) ? chat.messages : [],
    options: Array.isArray(chat.options) ? chat.options : [],
    finalRes: chat.finalRes ?? null,
    conversationStarted: Boolean(chat.conversationStarted),
    autoNoSent: Boolean(chat.autoNoSent),
    updatedAt: chat.updatedAt || null
})

const CHAT_TITLE_MAX_LENGTH = 80

const truncateText = (value, limit = CHAT_TITLE_MAX_LENGTH) => {
    const text = String(value || '').trim()
    if (!text) return ''
    if (text.length <= limit) return text
    return `${text.slice(0, limit).trimEnd()}...`
}

const getFirstUserMessage = (chat) => {
    if (!Array.isArray(chat?.messages)) return ''

    const firstUserMessage = chat.messages.find(message => message?.sender === 'user')
    return String(firstUserMessage?.message?.message || '').trim()
}

const getChatTitle = (chat) => {
    const item = chat?.itemName?.trim()
    const country = chat?.targetCountry?.trim()
    const firstUserMessage = getFirstUserMessage(chat)

    if (item && country) return `${item} - ${country}`
    if (item) return item
    if (country) return country
    if (firstUserMessage) return firstUserMessage
    return chat?.title || 'New chat'
}

const AiHsCalculator = () => {
    const [chatList, setChatList] = useState([])
    const [activeChatId, setActiveChatId] = useState(null)
    const [activeChat, setActiveChat] = useState(null)
    const [resultData, setResultData] = useState(null)
    const [loadingChats, setLoadingChats] = useState(true)
    const [loadingActiveChat, setLoadingActiveChat] = useState(false)
    const [chatPendingDelete, setChatPendingDelete] = useState(null)
    const saveTimeoutRef = useRef(null)
    const initializedRef = useRef(false)

    const updateChatSummary = useCallback((chat) => {
        const normalized = normalizeChat(chat)

        setChatList(prev => {
            const next = prev.filter(item => item._id !== normalized._id)
            return [{
                ...normalized,
                title: getChatTitle(normalized),
            }, ...next]
        })
    }, [])

    const loadChat = useCallback(async (chatId) => {
        if (!chatId) return

        try {
            setLoadingActiveChat(true)
            const res = await api.get(`/hs-compass/chats/${chatId}`)
            const normalized = normalizeChat(res.data?.data || {})
            setActiveChatId(normalized._id)
            setActiveChat(normalized)
            setResultData(normalized.finalRes || null)
        } catch (error) {
            console.error('Failed to load HS Compass chat:', error)
            toast.error('Unable to load this chat')
        } finally {
            setLoadingActiveChat(false)
        }
    }, [])

    const createNewChat = useCallback(async () => {
        try {
            const res = await api.post('/hs-compass/chats', {})
            const normalized = normalizeChat(res.data?.data || {})
            setActiveChatId(normalized._id)
            setActiveChat(normalized)
            setResultData(null)
            updateChatSummary(normalized)
            return normalized
        } catch (error) {
            console.error('Failed to create HS Compass chat:', error)
            toast.error('Unable to start a new chat')
            return null
        }
    }, [updateChatSummary])

    const handleDeleteChat = useCallback(async (chatId) => {
        if (!chatId) return

        try {
            await api.delete(`/hs-compass/chats/${chatId}`)

            const remainingChats = chatList.filter(chat => chat._id !== chatId)
            setChatList(remainingChats)

            if (activeChatId === chatId) {
                if (remainingChats.length > 0) {
                    await loadChat(remainingChats[0]._id)
                } else {
                    await createNewChat()
                }
            }

            toast.success('Chat deleted')
        } catch (error) {
            console.error('Failed to delete HS Compass chat:', error)
            toast.error('Unable to delete this chat')
        }
    }, [activeChatId, chatList, createNewChat, loadChat])

    useEffect(() => {
        if (initializedRef.current) return
        initializedRef.current = true

        const initializeChats = async () => {
            try {
                setLoadingChats(true)
                const res = await api.get('/hs-compass/chats')
                const chats = Array.isArray(res.data?.data) ? res.data.data.map(normalizeChat) : []
                setChatList(chats)

                if (chats.length > 0) {
                    await loadChat(chats[0]._id)
                } else {
                    await createNewChat()
                }
            } catch (error) {
                console.error('Failed to fetch HS Compass chats:', error)
                toast.error('Unable to load chat history')
            } finally {
                setLoadingChats(false)
            }
        }

        initializeChats()
    }, [createNewChat, loadChat])

    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        }
    }, [])

    const persistActiveChat = useCallback((nextState) => {
        if (!nextState?._id) return

        const normalized = normalizeChat(nextState)
        const title = getChatTitle(normalized)

        setResultData(normalized.finalRes || null)

        setChatList(prev => {
            const current = prev.filter(item => item._id !== normalized._id)
            return [{
                ...normalized,
                title,
            }, ...current]
        })

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await api.put(`/hs-compass/chats/${normalized._id}`, {
                    title,
                    itemName: normalized.itemName,
                    targetCountry: normalized.targetCountry,
                    sessionId: normalized.sessionId,
                    messages: normalized.messages,
                    options: normalized.options,
                    finalRes: normalized.finalRes,
                    conversationStarted: normalized.conversationStarted,
                    autoNoSent: normalized.autoNoSent,
                })
            } catch (error) {
                console.error('Failed to save HS Compass chat:', error)
            }
        }, 500)
    }, [])

    const chatItems = useMemo(() => chatList.map(chat => ({
        ...chat,
        title: getChatTitle(chat),
        shortTitle: truncateText(getChatTitle(chat)),
        preview: chat.messages?.length
            ? truncateText(chat.messages[chat.messages.length - 1]?.message?.message || '', 60)
            : 'No messages yet'
    })), [chatList])

    const shouldUseCompactCard = !activeChat?.conversationStarted && !activeChat?.messages?.length && !resultData

    return (
        <div className="w-full flex items-center justify-center p-3 md:p-4">
            <div className="w-full flex flex-col items-center justify-center gap-6">
                  <div className="w-full bg-white border rounded-xl custom-shadow overflow-hidden h-[calc(100vh-175px)] min-h-[620px]">
                    <div className="h-full flex flex-col md:flex-row">
                        <aside className="w-full md:w-[240px] lg:w-[260px] border-b md:border-b-0 md:border-r bg-[#f8fafc] flex flex-col">
                            <div className="p-4 border-b flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold text-[#03336e]">Chat History</p>
                                  
                                </div>
                                <button
                                    type="button"
                                    onClick={createNewChat}
                                    className="inline-flex items-center gap-2 rounded-lg bg-[#03336e] px-3 py-2 text-sm font-medium text-white hover:bg-[#024a9e] transition-colors"
                                >
                                    <Plus size={16} />
                                    New Chat
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                                {loadingChats ? (
                                    <p className="text-sm text-slate-500 px-2 py-3">Loading chats...</p>
                                ) : chatItems.map(chat => (
                                    <div
                                        key={chat._id}
                                        className={`group rounded-xl border px-3 py-3 transition-colors ${chat._id === activeChatId ? 'border-[#03336e] bg-white shadow-sm' : 'border-slate-200 bg-white/70 hover:bg-white'}`}
                                    >
                                        <div className="flex items-start gap-2 min-w-0">
                                            <button
                                                type="button"
                                                onClick={() => loadChat(chat._id)}
                                                className="flex-1 min-w-0 max-w-full text-left pr-1"
                                                title={chat.shortTitle}
                                            >
                                                <p
                                                    className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold text-slate-800"
                                                    title={chat.shortTitle}
                                                >
                                                    {chat.shortTitle}
                                                </p>
                                                <p
                                                    className="mt-1 block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-500"
                                                    title={chat.preview || 'No messages yet'}
                                                >
                                                    {chat.preview || 'No messages yet'}
                                                </p>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setChatPendingDelete(chat)
                                                }}
                                                className="mt-0.5 shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                                aria-label={`Delete ${chat.title}`}
                                                title="Delete chat"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </aside>

                        <div className="flex-1 min-w-0 flex flex-col">
                            {loadingActiveChat || !activeChat ? (
                                <div className="h-full flex items-center justify-center text-slate-500">
                                    Loading chat...
                                </div>
                            ) : (
                                <AiHsCalculatorChat
                                    key={activeChat._id}
                                    activeChat={activeChat}
                                    onPersistChat={persistActiveChat}
                                    onCreateNewChat={createNewChat}
                                    setResultData={setResultData}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {resultData && (
                    <div className="w-full bg-white border rounded-xl custom-shadow p-5 h-full overflow-y-auto">
                        <ResultCard apiResponse={resultData} />
                    </div>
                )}

                {chatPendingDelete && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                            <h3 className="text-lg font-semibold text-slate-900">Delete chat?</h3>
                            <p className="mt-2 text-sm text-slate-600">
                                Are you sure you want to delete
                                {' '}
                                <span className="font-medium text-slate-800">{truncateText(chatPendingDelete.title)}</span>
                                ?
                            </p>
                            <div className="mt-6 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setChatPendingDelete(null)}
                                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                                >
                                    No
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        const chatId = chatPendingDelete?._id
                                        setChatPendingDelete(null)
                                        await handleDeleteChat(chatId)
                                    }}
                                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AiHsCalculator
